import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getFieldLabel,
  getFieldContainer,
  shouldSkipInListView,
  isFileFieldType,
  extractSubformData,
  getTabsFields,
  getTableFields,
} from '@iqvia-technology-solutions/formcrafter';
import FieldRow from './ListView/FieldRow';
import FieldValueRenderer from './ListView/FieldValueRenderer';
import useListViewFieldEditing from '../../../hooks/ui/listView/useListViewFieldEditing';
import useListViewComments from '../../../hooks/ui/listView/useListViewComments';
import useListViewCascadeEditing from '../../../hooks/ui/listView/useListViewCascadeEditing';
import { parseFormData } from '../../../utils/formCrafter/formDataUtils';
import {
  resolveFormId,
  processFileMetadata,
  parseSubformSchema,
  canEditFieldType,
  getSubformsToRender,
  getEffectiveValues,
  getEffectiveFieldType,
  getEffectiveField,
  isFieldVisibleInApplicantView,
  isFieldVisibleInReviewerView,
  formatRepeaterDisplayValue,
} from '../../../utils/formCrafter/listViewUtils';
import { isFieldVisible } from '../../../utils/formCrafter/visibilityUtils';
import { FIELD_TYPES } from '../../../constants/formCrafter.constant';
import useFieldWavesApi from '../../../hooks/api/fieldWaves/useFieldWavesApi';
import ChartLine from 'apollo-react-icons/ChartLine';
import FieldWavesModal from '../FieldWaves/FieldWavesModal';
import SubformCommentSection from '../Comments/SubformCommentSection/SubformCommentSection';
import StyledTabs from '../StyledTabs/StyledTabs';
import styles from './FormCrafterListView.module.css';

const RepeaterWavesAction = ({
  requestId,
  fieldKey,
  fieldLabel,
  repeaterFields,
  hasWaves,
  enableFieldWaves,
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  if ((!enableFieldWaves && !hasWaves) || !requestId || !fieldKey) return null;

  return (
    <div className={styles.repeaterHeaderActions}>
      <button
        type="button"
        className={styles.repeaterWavesButton}
        onClick={() => setIsModalOpen(true)}
        title={t('fieldActions.fieldWaves')}
      >
        <ChartLine className={styles.repeaterWavesIcon} />
      </button>
      <FieldWavesModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        requestId={requestId}
        fieldKey={fieldKey}
        fieldLabel={fieldLabel}
        repeaterFields={repeaterFields}
      />
    </div>
  );
};

const ListViewTabsPanel = ({ field, tabsFields, renderTabField }) => {
  const tabs = field.tabs || [];
  const tabCount = tabs.length;
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (tabCount > 0 && activeTab >= tabCount) {
      setActiveTab(tabCount - 1);
    }
  }, [tabCount, activeTab]);

  const safeActiveTab = tabCount > 0 ? Math.min(activeTab, tabCount - 1) : 0;

  const handleTabChange = useCallback(
    (_, newValue) => {
      setActiveTab(Math.max(0, Math.min(Number(newValue), tabCount - 1)));
    },
    [tabCount]
  );

  const fieldsByTab = useMemo(() => {
    const grouped = {};
    tabsFields.forEach((tf) => {
      const tabId = tf._tabId;
      if (!grouped[tabId]) grouped[tabId] = [];
      grouped[tabId].push(tf);
    });
    return grouped;
  }, [tabsFields]);

  if (tabCount === 0) return null;

  const activeTabDef = tabs[safeActiveTab];
  const activeFields = activeTabDef ? fieldsByTab[activeTabDef.id] || [] : [];

  return (
    <div className={styles.listViewTabsContainer}>
      <StyledTabs
        value={safeActiveTab}
        onChange={handleTabChange}
        variant={tabCount > 4 ? 'scrollable' : undefined}
        tabs={tabs.map((tab) => ({
          key: tab.id,
          label: tab.label,
          showStar: true,
        }))}
      />
      <div role="tabpanel" className={styles.listViewTabPanel}>
        {activeFields.map(renderTabField)}
      </div>
    </div>
  );
};

/**
 * FormCrafterListView - Displays FormCrafter forms as key-value pairs
 *
 * Refactored to use atomic design with custom hooks and utility functions.
 *
 * @param {Array} subforms - Array of subform objects with json_object and id
 * @param {Object} filledForm - Previously filled form data
 * @param {Array} fileMetadata - Array of file metadata
 * @param {Array} comments - Array of comments
 * @param {Function} setComments - Function to update comments
 * @param {boolean} enableAddComment - Whether to enable adding comments
 * @param {boolean} showHiddenFields - Whether to show hidden fields
 * @param {number} activeSubformIndex - If provided, only renders the subform at this index
 * @param {number|string} formId - Form ID to prevent overlapping comments
 * @param {boolean} enableFieldEdit - Whether to enable inline field editing
 * @param {Object} formRendererRef - Ref to form renderer for value sync
 * @param {Function} markPendingEdit - Callback when edit starts
 * @param {Function} markResolvedEdit - Callback when edit ends
 * @param {boolean} showRequiredIndicator - Whether to show required asterisk
 * @param {number|string} requestId - Request ID for field waves feature
 * @param {boolean} enableFieldWaves - Whether to enable field waves viewing
 */
const FormCrafterListView = ({
  subforms = [],
  filledForm,
  fileMetadata = [],
  comments = [],
  setComments,
  enableAddComment = false,
  showHiddenFields = false,
  activeSubformIndex,
  formId,
  enableFieldEdit = false,
  formRendererRef,
  markPendingEdit,
  markResolvedEdit,
  showRequiredIndicator = false,
  requestId,
  enableFieldWaves = false,
  onStagedFileUpload,
  onStagedFileDelete,
  eagerUploadEnabled = false,
  stagedFilesByField = {},
  draftDeletedFileIds = [],
  onServerFileDeleteConfirmed,
  requestProgress,
  requestDateEnded,
  currentUserEmail,
  onCommentUpdated,
  onCommentDeleted,
  onSubformCommentAdded,
  allowDeletedFiles = false,
}) => {
  const { t } = useTranslation();
  // Resolve form ID from props or filledForm
  const resolvedFormId = useMemo(
    () => resolveFormId(formId, filledForm),
    [formId, filledForm]
  );

  // Parse form data
  const formData = useMemo(
    () => parseFormData(filledForm, resolvedFormId),
    [filledForm, resolvedFormId]
  );

  // Process file metadata into lookup map
  const fileMetadataByField = useMemo(
    () => processFileMetadata(fileMetadata, filledForm, resolvedFormId),
    [fileMetadata, filledForm, resolvedFormId]
  );

  // Fetch field IDs with waves for this request (batch, once)
  const { getFieldsWithWaves } = useFieldWavesApi();
  const [fieldsWithWavesSet, setFieldsWithWavesSet] = useState(() => new Set());
  useEffect(() => {
    if (!enableFieldWaves || !requestId) return;
    let cancelled = false;
    getFieldsWithWaves(requestId)
      .then((ids) => {
        if (!cancelled) setFieldsWithWavesSet(new Set(ids));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [requestId, enableFieldWaves, getFieldsWithWaves]);

  // Field editing hook
  const editing = useListViewFieldEditing({
    formId: resolvedFormId,
    markPendingEdit,
    markResolvedEdit,
    formRendererRef,
  });

  // Comments hook
  const commentState = useListViewComments({
    comments,
    setComments,
    formId: resolvedFormId,
    filledFormId: filledForm?.id,
    enableAddComment,
    requestId,
  });

  // Filter subforms to render (needed for cascade hook)
  const subformsToRender = useMemo(
    () => getSubformsToRender(subforms, activeSubformIndex),
    [subforms, activeSubformIndex]
  );

  // Cascade editing hook
  const { buildEditActions } = useListViewCascadeEditing({
    editing,
    subformsToRender,
    formData,
    formId: resolvedFormId,
    showHiddenFields,
    enableFieldEdit,
    isFileFieldType,
  });

  const getStagedFilesForField = useCallback(
    (subformId, fieldId, includeNestedFields = false) => {
      if (!stagedFilesByField) return [];

      // stagedFilesByField (draftStagedFiles) uses key format: "formId-subformId-fieldId" or "subformId-fieldId"
      const exactKey = resolvedFormId
        ? `${resolvedFormId}-${subformId}-${fieldId}`
        : `${subformId}-${fieldId}`;

      const normalizeFile = (file) => ({
        id: `staged-${file.id || file.stagedId}`,
        stagedId: file.id || file.stagedId,
        name: file.name,
        mimeType: file.type || file.mimeType,
        isStagedFile: true,
        fieldId: file.fieldId || file.field_id || file.context?.field_id,
      });

      // For exact match
      const exactMatch = stagedFilesByField[exactKey] || [];

      if (!includeNestedFields) {
        return exactMatch.map(normalizeFile);
      }

      // For repeater fields, also look for keys that start with fieldId-
      // This handles repeater item files like "attachment_field-1707856123456"
      const allFiles = [...exactMatch];
      const keyPrefix = resolvedFormId
        ? `${resolvedFormId}-${subformId}-${fieldId}-`
        : `${subformId}-${fieldId}-`;

      Object.entries(stagedFilesByField).forEach(([key, files]) => {
        if (key.startsWith(keyPrefix)) {
          allFiles.push(...files);
        }
      });

      return allFiles.map(normalizeFile);
    },
    [resolvedFormId, stagedFilesByField]
  );

  const getRepeaterFieldIdCandidates = useCallback(
    (baseFieldId, itemId, itemIndex) => {
      const itemKeys = [];
      if (itemId !== undefined && itemId !== null && itemId !== '') {
        itemKeys.push(itemId);
      }
      if (itemIndex !== undefined && itemIndex !== null && itemIndex !== '') {
        itemKeys.push(itemIndex);
      }

      const uniqueItemKeys = Array.from(new Set(itemKeys.map(String)));
      const candidates = [];
      uniqueItemKeys.forEach((key) => {
        candidates.push(`${baseFieldId}-${key}`);
        candidates.push(`${baseFieldId}__item_${key}`);
      });

      return Array.from(new Set(candidates));
    },
    []
  );

  const dedupeFilesById = useCallback((files) => {
    const seen = new Set();
    return (files || []).filter((file) => {
      if (!file?.id) return true;
      if (seen.has(file.id)) return false;
      seen.add(file.id);
      return true;
    });
  }, []);

  const getRepeaterServerFiles = useCallback(
    (subformId, baseFieldId, itemId, itemIndex) => {
      const fieldIds = getRepeaterFieldIdCandidates(
        baseFieldId,
        itemId,
        itemIndex
      );
      const files = [];

      fieldIds.forEach((fieldId) => {
        const key = `${String(subformId || '')}_${fieldId}`;
        const matchedFiles = fileMetadataByField[key] || [];
        if (matchedFiles.length > 0) {
          files.push(...matchedFiles);
        }
      });

      return dedupeFilesById(files);
    },
    [dedupeFilesById, fileMetadataByField, getRepeaterFieldIdCandidates]
  );

  const getRepeaterStagedFiles = useCallback(
    (subformId, baseFieldId, itemId, itemIndex) => {
      const fieldIds = getRepeaterFieldIdCandidates(
        baseFieldId,
        itemId,
        itemIndex
      );
      const files = [];

      fieldIds.forEach((fieldId) => {
        const matchedFiles = getStagedFilesForField(subformId, fieldId);
        if (matchedFiles.length > 0) {
          files.push(...matchedFiles);
        }
      });

      return dedupeFilesById(files);
    },
    [dedupeFilesById, getRepeaterFieldIdCandidates, getStagedFilesForField]
  );

  // Create a handler that tracks deletion both for backend submission AND for display filtering.
  // Supports both single fileId and array of fileIds (bulk deletion from attachment fields).
  const createServerFileDeleteHandler = useCallback(
    (subformId, fieldId) => async (fileIdOrIds) => {
      if (!fileIdOrIds) return;
      // Normalize to array for uniform handling
      const fileIds = Array.isArray(fileIdOrIds) ? fileIdOrIds : [fileIdOrIds];
      if (fileIds.length === 0) return;

      // Mark each file for backend deletion and display filtering
      fileIds.forEach((fid) => {
        if (formRendererRef?.current?.markDeletedFileId) {
          formRendererRef.current.markDeletedFileId(fid);
        }
        editing.markServerFileDeleted(subformId, fieldId, fid);
      });

      // Call parent handler with the original format (array)
      if (onServerFileDeleteConfirmed) {
        await onServerFileDeleteConfirmed(fileIds);
      }
    },
    [formRendererRef, editing, onServerFileDeleteConfirmed]
  );

  // Check if field can be edited
  const canEdit = useCallback(
    (fieldType, field = null) =>
      canEditFieldType(fieldType, enableFieldEdit, isFileFieldType, field),
    [enableFieldEdit]
  );

  // Build comment props for a field
  const buildCommentProps = useCallback(
    (
      subformId,
      fieldId,
      fieldType,
      field = null,
      fieldKeyOverride = null,
      commentFieldIdOverride = null
    ) => {
      const fieldKey =
        fieldKeyOverride || editing.getFieldKey(subformId, fieldId);
      const commentFieldId = commentFieldIdOverride || fieldId;
      const fieldComments = commentState.getCommentsForField(
        subformId,
        commentFieldId
      );

      // Hidden fields cannot have comments, field waves, or editing (reviewer-only visibility, read-only)
      const isHiddenField = field?.type === FIELD_TYPES.HIDDEN;
      const effectiveEnableAddComment = isHiddenField
        ? false
        : enableAddComment;
      const effectiveEnableFieldWaves = isHiddenField
        ? false
        : enableFieldWaves;
      const effectiveEnableFieldEdit = isHiddenField ? false : enableFieldEdit;
      const effectiveCanEdit = isHiddenField
        ? false
        : canEdit(fieldType, field);

      return {
        comments: fieldComments,
        enableAddComment: effectiveEnableAddComment,
        enableFieldEdit: effectiveEnableFieldEdit,
        canEdit: effectiveCanEdit,
        onAddComment: commentState.handleAddComment,
        activeQuickCommentField: commentState.activeQuickCommentField,
        activeCommentsModalField: commentState.activeCommentsModalField,
        onQuickCommentToggle: commentState.handleQuickCommentToggle,
        onCommentsModalToggle: commentState.handleCommentsModalToggle,
        onCloseQuickComment: commentState.closeQuickComment,
        onOpenCommentsModal: commentState.openCommentsModal,
        onCommentAdded: (newComment) =>
          commentState.handleCommentAdded(
            subformId,
            commentFieldId,
            newComment,
            fieldKey
          ),
        lastAddedComment: fieldKey
          ? commentState.lastAddedComments[fieldKey]
          : null,
        // Field waves props
        requestId,
        formId: resolvedFormId,
        enableFieldWaves: effectiveEnableFieldWaves,
        hasWaves: fieldsWithWavesSet.has(fieldKey),
        fieldKeyOverride: fieldKey,
        fieldIdOverride: commentFieldId,
        // Hidden field indicator for UX message
        isHiddenField,
        // Comment edit/delete props
        requestProgress,
        requestDateEnded,
        currentUserEmail,
        onCommentUpdated:
          onCommentUpdated ||
          (setComments
            ? (updatedComment) => {
                setComments((prev) =>
                  prev.map((c) =>
                    c.comment_id === updatedComment.id ||
                    c.comment?.id === updatedComment.id
                      ? { ...c, comment: updatedComment }
                      : c
                  )
                );
              }
            : undefined),
        onCommentDeleted:
          onCommentDeleted ||
          (setComments
            ? (commentId) => {
                setComments((prev) =>
                  prev.filter(
                    (c) =>
                      c.comment_id !== commentId && c.comment?.id !== commentId
                  )
                );
              }
            : undefined),
      };
    },
    [
      editing,
      commentState,
      enableAddComment,
      enableFieldEdit,
      canEdit,
      requestId,
      resolvedFormId,
      enableFieldWaves,
      fieldsWithWavesSet,
      requestProgress,
      requestDateEnded,
      currentUserEmail,
      onCommentUpdated,
      onCommentDeleted,
      setComments,
    ]
  );

  // Render a standard field row
  const renderField = useCallback(
    (subformId, field, subformData, labelPrefix = '') => {
      const fieldKey = editing.getFieldKey(subformId, field.id);
      const rawValue = subformData[field.id];
      const displayValue = editing.getEditedValue(
        subformId,
        field.id,
        rawValue
      );
      const isRequired = field.required || field.validation?.required;
      const label = labelPrefix
        ? `${labelPrefix} - ${getFieldLabel(field.id, { fields: [field] })}`
        : getFieldLabel(field.id, { fields: [field] });

      // Get effective field for rendering (hidden with conditions -> text)
      const effectiveField = getEffectiveField(field);
      const effectiveFieldType = getEffectiveFieldType(field);

      const key = `${String(subformId || '')}_${field.id}`;
      const serverFiles = fileMetadataByField[key] || [];
      const stagedFiles = getStagedFilesForField(subformId, field.id);
      // Show both server files and staged files together (append behavior)
      const combinedFiles = [...serverFiles, ...stagedFiles];

      const combinedDeletedFileIds = [
        ...editing.getDeletedServerFileIds(subformId, field.id),
        ...draftDeletedFileIds,
      ];

      return (
        <FieldRow
          key={field.id}
          fieldKey={fieldKey}
          label={label}
          value={rawValue}
          displayValue={displayValue}
          field={effectiveField}
          subformId={subformId}
          subformData={subformData}
          files={combinedFiles}
          allowDeletedFiles={allowDeletedFiles}
          serverFiles={serverFiles}
          stagedFiles={stagedFiles}
          onServerFileDelete={createServerFileDeleteHandler(
            subformId,
            field.id
          )}
          onStagedFileUpload={onStagedFileUpload}
          onStagedFileDelete={onStagedFileDelete}
          eagerUploadEnabled={eagerUploadEnabled}
          deletedFileIds={combinedDeletedFileIds}
          showHiddenFields={showHiddenFields}
          showRequiredIndicator={showRequiredIndicator}
          isRequired={isRequired}
          isEditing={editing.isEditing(subformId, field.id)}
          draftValue={editing.getDraftValue(subformId, field.id)}
          editActions={buildEditActions(subformId, field.id, subformData)}
          commentProps={buildCommentProps(
            subformId,
            field.id,
            effectiveFieldType,
            field
          )}
        />
      );
    },
    [
      editing,
      fileMetadataByField,
      getStagedFilesForField,
      showHiddenFields,
      showRequiredIndicator,
      buildEditActions,
      buildCommentProps,
      createServerFileDeleteHandler,
      onStagedFileUpload,
      onStagedFileDelete,
      eagerUploadEnabled,
      draftDeletedFileIds,
    ]
  );

  // Render table container fields
  const renderTableFields = useCallback(
    (field, schema, subformId, subformData) => {
      const tableFields = getTableFields(field, schema.fields);
      if (tableFields.length === 0) return null;

      return (
        <React.Fragment key={field.id}>
          {tableFields.map((tableField) => {
            const columnLabel =
              tableField._columnLabel || tableField._columnKey;
            return renderField(subformId, tableField, subformData, columnLabel);
          })}
        </React.Fragment>
      );
    },
    [renderField]
  );

  // Render repeater fields
  const renderRepeaterFields = useCallback(
    (field, schema, subformId, subformData) => {
      const rawValue = subformData[field.id];
      const displayValue = editing.getEditedValue(
        subformId,
        field.id,
        rawValue
      );
      const repeaterLabel = getFieldLabel(field.id, schema);
      const isRequired = field.required || field.validation?.required;
      const items = Array.isArray(displayValue) ? displayValue : [];
      const nestedFields = Array.isArray(field.fields) ? field.fields : [];
      const repeaterFieldKey = editing.getFieldKey(subformId, field.id);
      const repeaterCommentProps = buildCommentProps(
        subformId,
        field.id,
        FIELD_TYPES.REPEATER,
        field
      );
      const repeaterEditActions = buildEditActions(
        subformId,
        field.id,
        subformData
      );
      const isRepeaterEditing = editing.isEditing(subformId, field.id);
      const handleRepeaterEdit = () => {
        if (!repeaterEditActions?.onStart) return;
        const initialValue = Array.isArray(displayValue) ? displayValue : [];
        repeaterEditActions.onStart(initialValue);
      };

      const getRepeaterItemValue = (item, fieldId) => {
        if (!item || typeof item !== 'object') return undefined;
        if (fieldId in item) return item[fieldId];
        if (
          item.value &&
          typeof item.value === 'object' &&
          item.value !== null &&
          fieldId in item.value
        ) {
          return item.value[fieldId];
        }
        return undefined;
      };

      const getRepeaterItemValues = (item) => {
        if (!item || typeof item !== 'object') return {};
        if (
          item.value &&
          typeof item.value === 'object' &&
          item.value !== null
        ) {
          return item.value;
        }
        return item;
      };

      const getRepeaterItemLabel = (item, index) => {
        const labelFieldId =
          field?.itemLabelFieldId ||
          field?.itemLabelField ||
          field?.labelFieldId ||
          field?.labelField;
        const labelValue =
          (labelFieldId && getRepeaterItemValue(item, labelFieldId)) ||
          item?.item_field ||
          item?.label ||
          item?.name;
        const fallback = t('formRenderer.repeaterItem', { index: index + 1 });
        if (!labelValue || labelValue === '') return fallback;
        return `${fallback}: ${labelValue}`;
      };

      // Collect all files for nested fields within this repeater (for edit mode)
      // Also collect deleted file IDs to filter them out from display
      const repeaterExistingFiles = [];
      const repeaterStagedFiles = [];
      const repeaterDeletedFileIds = [];
      // Include deletions tracked against the repeater field itself (from edit mode)
      const repeaterFieldDeletedIds = editing.getDeletedServerFileIds(
        subformId,
        field.id
      );
      repeaterDeletedFileIds.push(...repeaterFieldDeletedIds);
      items.forEach((item, itemIndex) => {
        const itemKey = item?.id ?? itemIndex;
        nestedFields.forEach((nestedField) => {
          const nestedItemFieldId = `${nestedField.id}-${itemKey}`;
          const nestedServerFiles = getRepeaterServerFiles(
            subformId,
            nestedField.id,
            item?.id,
            itemIndex
          );
          const nestedStagedFilesForItem = getRepeaterStagedFiles(
            subformId,
            nestedField.id,
            item?.id,
            itemIndex
          );
          // Collect deleted file IDs for this nested field
          const nestedDeletedIds = editing.getDeletedServerFileIds(
            subformId,
            nestedItemFieldId
          );
          repeaterDeletedFileIds.push(...nestedDeletedIds);
          // Add fieldId to each file for proper association
          nestedServerFiles.forEach((f) => {
            repeaterExistingFiles.push({
              ...f,
              fieldId: nestedItemFieldId,
              // eslint-disable-next-line camelcase
              field_id: nestedItemFieldId,
            });
          });
          nestedStagedFilesForItem.forEach((f) => {
            repeaterStagedFiles.push({
              ...f,
              fieldId: nestedItemFieldId,
              // eslint-disable-next-line camelcase
              field_id: nestedItemFieldId,
            });
          });
        });
      });
      // Combine with draft deleted file IDs
      const allRepeaterDeletedFileIds = [
        ...repeaterDeletedFileIds,
        ...draftDeletedFileIds,
      ];
      // Filter out deleted files from existing files
      const filteredRepeaterExistingFiles = repeaterExistingFiles.filter(
        (f) => !allRepeaterDeletedFileIds.includes(f.id)
      );

      if (items.length === 0) {
        return (
          <FieldRow
            key={field.id}
            fieldKey={repeaterFieldKey}
            label={repeaterLabel}
            value={displayValue || []}
            displayValue={formatRepeaterDisplayValue(displayValue, t)}
            field={field}
            subformId={subformId}
            subformData={subformData}
            files={null}
            serverFiles={[]}
            stagedFiles={[]}
            onServerFileDelete={createServerFileDeleteHandler(
              subformId,
              field.id
            )}
            onStagedFileUpload={onStagedFileUpload}
            onStagedFileDelete={onStagedFileDelete}
            eagerUploadEnabled={eagerUploadEnabled}
            deletedFileIds={draftDeletedFileIds}
            showHiddenFields={showHiddenFields}
            showRequiredIndicator={showRequiredIndicator}
            isRequired={isRequired}
            isEditing={isRepeaterEditing}
            draftValue={editing.getDraftValue(subformId, field.id)}
            editActions={repeaterEditActions}
            commentProps={repeaterCommentProps}
          />
        );
      }

      return (
        <div key={field.id} className={styles.repeaterGroup}>
          <div className={styles.repeaterGroupHeader}>
            <div className={styles.repeaterGroupTitle}>
              {showRequiredIndicator && isRequired && (
                <span className={styles.requiredAsterisk}>*</span>
              )}
              <span className={styles.repeaterGroupLabel}>{repeaterLabel}</span>
            </div>
            <RepeaterWavesAction
              requestId={requestId}
              fieldKey={repeaterFieldKey}
              fieldLabel={repeaterLabel}
              repeaterFields={field.fields}
              hasWaves={repeaterCommentProps.hasWaves}
              enableFieldWaves={enableFieldWaves}
            />
          </div>
          {isRepeaterEditing && (
            <FieldRow
              key={`${field.id}-editor`}
              fieldKey={repeaterFieldKey}
              label={repeaterLabel}
              value={displayValue || []}
              displayValue={displayValue}
              field={field}
              subformId={subformId}
              subformData={subformData}
              files={[...filteredRepeaterExistingFiles, ...repeaterStagedFiles]}
              serverFiles={filteredRepeaterExistingFiles}
              stagedFiles={repeaterStagedFiles}
              onServerFileDelete={createServerFileDeleteHandler(
                subformId,
                field.id
              )}
              onStagedFileUpload={onStagedFileUpload}
              onStagedFileDelete={onStagedFileDelete}
              eagerUploadEnabled={eagerUploadEnabled}
              deletedFileIds={allRepeaterDeletedFileIds}
              showHiddenFields={showHiddenFields}
              showRequiredIndicator={showRequiredIndicator}
              isRequired={isRequired}
              isEditing={isRepeaterEditing}
              draftValue={editing.getDraftValue(subformId, field.id)}
              editActions={repeaterEditActions}
              commentProps={repeaterCommentProps}
            />
          )}
          {items.map((item, itemIndex) => {
            const itemKey = item?.id ?? itemIndex;
            const itemValues = getRepeaterItemValues(item);

            return (
              <div
                key={`${field.id}-${itemKey}`}
                className={styles.repeaterItemGroup}
              >
                <div className={styles.repeaterItemHeader}>
                  <span className={styles.repeaterItemTitle}>
                    {getRepeaterItemLabel(item, itemIndex)}
                  </span>
                </div>
                <div className={styles.repeaterItemFields}>
                  {nestedFields.length === 0 ? (
                    <div className={styles.repeaterItemFieldRow}>
                      <FieldRow
                        fieldKey={`${field.id}-${itemKey}-value`}
                        label={t('formRenderer.repeaterItem', {
                          index: itemIndex + 1,
                        })}
                        value={item?.value ?? item}
                        displayValue={item?.value ?? item}
                        field={field}
                        subformId={subformId}
                        subformData={itemValues}
                        files={null}
                        showHiddenFields={showHiddenFields}
                        showRequiredIndicator={false}
                        isRequired={false}
                        isEditing={false}
                        draftValue={undefined}
                        editActions={{}}
                        commentProps={{
                          enableAddComment: false,
                          enableFieldEdit: false,
                          canEdit: false,
                          enableFieldWaves: false,
                        }}
                      />
                    </div>
                  ) : (
                    // For Repeaters fields
                    nestedFields.map((nestedField) => {
                      if (!nestedField || !nestedField.id) return null;
                      if (shouldSkipInListView(nestedField.type)) return null;

                      const nestedValue = getRepeaterItemValue(
                        item,
                        nestedField.id
                      );
                      const effectiveNestedField =
                        getEffectiveField(nestedField);
                      const effectiveNestedType =
                        getEffectiveFieldType(nestedField);

                      const isVisible = showHiddenFields
                        ? isFieldVisibleInReviewerView(
                            nestedField,
                            itemValues,
                            isFieldVisible
                          )
                        : isFieldVisibleInApplicantView(
                            nestedField,
                            itemValues,
                            isFieldVisible
                          );

                      if (!isVisible) return null;

                      const nestedLabel = getFieldLabel(nestedField.id, {
                        fields: nestedFields,
                      });
                      const baseFieldKey = editing.getFieldKey(
                        subformId,
                        nestedField.id
                      );
                      const fieldKeyOverride = baseFieldKey
                        ? `${baseFieldKey}__item_${itemKey}`
                        : null;
                      const fieldWavesKeyOverride = repeaterFieldKey || null;
                      const commentFieldIdOverride = `${nestedField.id}__item_${itemKey}`;

                      // For repeater nested fields, use item-specific field ID for file lookups
                      // This ensures files are correctly scoped to each repeater item
                      const nestedItemFieldId = `${nestedField.id}-${itemKey}`;
                      const nestedServerFiles = getRepeaterServerFiles(
                        subformId,
                        nestedField.id,
                        item?.id,
                        itemIndex
                      );
                      const nestedStagedFiles = getRepeaterStagedFiles(
                        subformId,
                        nestedField.id,
                        item?.id,
                        itemIndex
                      );
                      // Show both server files and staged files together (append behavior)
                      const combinedNestedFiles = [
                        ...nestedServerFiles,
                        ...nestedStagedFiles,
                      ];

                      return (
                        <div
                          key={`${nestedField.id}-${itemKey}`}
                          className={styles.repeaterItemFieldRow}
                        >
                          <FieldRow
                            fieldKey={
                              fieldKeyOverride || `${nestedField.id}-${itemKey}`
                            }
                            label={nestedLabel}
                            value={nestedValue}
                            displayValue={nestedValue}
                            field={effectiveNestedField}
                            subformId={subformId}
                            subformData={itemValues}
                            files={combinedNestedFiles}
                            allowDeletedFiles={allowDeletedFiles}
                            serverFiles={nestedServerFiles}
                            stagedFiles={nestedStagedFiles}
                            onServerFileDelete={createServerFileDeleteHandler(
                              subformId,
                              nestedItemFieldId
                            )}
                            onStagedFileUpload={onStagedFileUpload}
                            onStagedFileDelete={onStagedFileDelete}
                            eagerUploadEnabled={eagerUploadEnabled}
                            deletedFileIds={[
                              ...editing.getDeletedServerFileIds(
                                subformId,
                                nestedItemFieldId
                              ),
                              ...repeaterFieldDeletedIds,
                            ]}
                            showHiddenFields={showHiddenFields}
                            showRequiredIndicator={showRequiredIndicator}
                            isRequired={
                              nestedField.required ||
                              nestedField.validation?.required
                            }
                            isEditing={false}
                            draftValue={undefined}
                            editActions={{}}
                            commentProps={{
                              ...buildCommentProps(
                                subformId,
                                nestedField.id,
                                effectiveNestedType,
                                nestedField,
                                fieldKeyOverride,
                                commentFieldIdOverride
                              ),
                              fieldWavesKeyOverride,
                              onEditFieldOverride: handleRepeaterEdit,
                              enableFieldWaves: false,
                              hasWaves: false,
                            }}
                          />
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    },
    [
      editing,
      showHiddenFields,
      showRequiredIndicator,
      buildCommentProps,
      buildEditActions,
      createServerFileDeleteHandler,
      getRepeaterServerFiles,
      getRepeaterStagedFiles,
      onStagedFileUpload,
      onStagedFileDelete,
      eagerUploadEnabled,
      draftDeletedFileIds,
      t,
    ]
  );

  // Render tabs container fields via ListViewTabsPanel (tab strip + active panel).
  // Defined after renderRepeaterFields so that repeaters nested inside tabs
  // can be fully expanded instead of collapsed into a one-line summary.
  const renderTabsFields = useCallback(
    (field, schema, subformId, subformData) => {
      const tabsFields = getTabsFields(field, schema.fields);
      if (tabsFields.length === 0) return null;

      const renderTabField = (tabField) => {
        if (shouldSkipInListView(tabField.type)) return null;

        if (tabField.type === FIELD_TYPES.REPEATER) {
          return renderRepeaterFields(tabField, schema, subformId, subformData);
        }

        return renderField(subformId, tabField, subformData);
      };

      return (
        <ListViewTabsPanel
          key={field.id}
          field={field}
          tabsFields={tabsFields}
          renderTabField={renderTabField}
        />
      );
    },
    [renderField, renderRepeaterFields]
  );

  // Render heading/paragraph fields
  const renderTextContent = useCallback(
    (field, subformId, value) => (
      <div key={field.id} className={styles.fieldRowContainer}>
        <div className={styles.fieldRow}>
          <div className={styles.fieldValueContainer}>
            <FieldValueRenderer
              subformId={subformId}
              fieldId={field.id}
              value={value}
              fieldType={field.type}
              field={field}
            />
          </div>
        </div>
      </div>
    ),
    []
  );

  return (
    <div className={styles.container}>
      {subformsToRender.map((subform) => {
        const schema = parseSubformSchema(subform);
        if (!subform?.id || !schema?.fields || schema.fields.length === 0) {
          return null;
        }

        const subformData = extractSubformData(formData, subform.id);

        // Compute effective values by overlaying inline edits/drafts
        const effectiveValues = getEffectiveValues(
          subformData,
          editing.editedValues,
          editing.editingDrafts,
          resolvedFormId,
          subform.id
        );

        return (
          <div key={subform.id} className={styles.subformSection}>
            {schema.fields.map((field) => {
              if (!field || !field.id) return null;

              try {
                const value = subformData[field.id];

                // Skip dividers
                if (shouldSkipInListView(field.type)) {
                  return null;
                }

                // Check visibility based on view mode
                const isVisible = showHiddenFields
                  ? isFieldVisibleInReviewerView(
                      field,
                      effectiveValues,
                      isFieldVisible
                    )
                  : isFieldVisibleInApplicantView(
                      field,
                      effectiveValues,
                      isFieldVisible
                    );

                if (!isVisible) {
                  return null;
                }

                // Skip container child fields
                const containerId = getFieldContainer(field.id, schema.fields);
                if (
                  containerId &&
                  ![FIELD_TYPES.TABS, FIELD_TYPES.TABLE].includes(field.type)
                ) {
                  return null;
                }

                // Handle tabs
                if (field.type === FIELD_TYPES.TABS) {
                  return renderTabsFields(
                    field,
                    schema,
                    subform.id,
                    subformData
                  );
                }

                // Handle tables
                if (field.type === FIELD_TYPES.TABLE) {
                  return renderTableFields(
                    field,
                    schema,
                    subform.id,
                    subformData
                  );
                }

                // Handle heading/paragraph
                if (
                  field.type === FIELD_TYPES.HEADING ||
                  field.type === FIELD_TYPES.PARAGRAPH
                ) {
                  const rendered = renderTextContent(field, subform.id, value);
                  if (!rendered) return null;
                  return rendered;
                }

                // Handle repeaters
                if (field.type === FIELD_TYPES.REPEATER) {
                  return renderRepeaterFields(
                    field,
                    schema,
                    subform.id,
                    subformData
                  );
                }

                // Standard field
                return renderField(subform.id, field, subformData);
              } catch (error) {
                console.error('Error rendering field:', error, field);
                return null;
              }
            })}

            {requestId && (
              <SubformCommentSection
                requestId={requestId}
                subFormId={subform.id}
                formId={resolvedFormId}
                requestProgress={requestProgress}
                requestDateEnded={requestDateEnded}
                currentUserEmail={currentUserEmail}
                enableAddComment={enableAddComment}
                onSubformCommentAdded={onSubformCommentAdded}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FormCrafterListView;
